
// src/pages/AuthCallback.js
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

// Capture URL params immediately at module load time, BEFORE detectSessionInUrl
// can consume the code and clear it from the URL.
const _initialUrl = window.location.href;
const _initialParams = new URL(_initialUrl).searchParams;
const _initialCode = _initialParams.get("code");
const _initialHash = window.location.hash;

export default function AuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const fromOnboarding = _initialParams.get("from") === "onboarding";

        // ── NATIVE APP RETURN: bounce PKCE code to native via deep link ──
        // When the native app starts OAuth, it sets redirectTo with ?native=1.
        // We must NOT exchange the code here (the web Supabase client has no
        // code_verifier — it's in Capacitor Preferences on the device).
        // Instead, immediately bounce the code to the native app.
        const isNativeReturn = _initialParams.get('native') === '1';
        if (isNativeReturn) {
          const isAndroid = /Android/i.test(navigator.userAgent);
          if (_initialCode) {
            const params = 'code=' + encodeURIComponent(_initialCode);
            if (isAndroid) {
              // intent:// URIs work reliably from Chrome Custom Tabs on Android
              window.location.replace(
                'intent://login?' + params +
                '#Intent;scheme=betterhusband;package=com.greathusband.app;end'
              );
            } else {
              // iOS handles custom scheme navigation from SFSafariViewController
              window.location.replace('betterhusband://login?' + params);
            }
          } else if (_initialHash) {
            // Fallback: tokens in hash fragment (implicit flow)
            const hashParams = new URLSearchParams(_initialHash.replace('#', ''));
            const at = hashParams.get('access_token');
            const rt = hashParams.get('refresh_token');
            if (at) {
              const params = 'access_token=' + encodeURIComponent(at) +
                '&refresh_token=' + encodeURIComponent(rt || '');
              if (isAndroid) {
                window.location.replace(
                  'intent://login?' + params +
                  '#Intent;scheme=betterhusband;package=com.greathusband.app;end'
                );
              } else {
                window.location.replace('betterhusband://login?' + params);
              }
            }
          }
          // Don't proceed with web auth flow — native app handles it
          return;
        }

        // ── Debug: log exactly what we received ──
        console.log('[AuthCallback] URL:', _initialUrl);
        console.log('[AuthCallback] code:', _initialCode ? 'present' : 'absent');
        console.log('[AuthCallback] hash:', _initialHash ? 'present' : 'absent');

        // ── Step 1: Give detectSessionInUrl 3 seconds to process ──
        console.log('[AuthCallback] Waiting 3s for detectSessionInUrl...');
        await new Promise(r => setTimeout(r, 3000));

        let session = null;
        const check1 = await supabase.auth.getSession();
        if (check1?.data?.session?.user?.id) {
          session = check1.data.session;
          console.log('[AuthCallback] Session found after detectSessionInUrl:', session.user.email);
        }

        // ── Step 2: If no session, try manual PKCE exchange ──
        if (!session?.user && _initialCode) {
          console.log('[AuthCallback] No session yet — trying manual PKCE exchange...');
          try {
            const { data, error } = await supabase.auth.exchangeCodeForSession(_initialCode);
            if (error) {
              console.warn('[AuthCallback] Manual PKCE exchange failed:', error.message);
            } else if (data?.session) {
              session = data.session;
              console.log('[AuthCallback] Session from manual exchange:', session.user.email);
            }
          } catch (err) {
            console.warn('[AuthCallback] Manual exchange exception:', err);
          }
        }

        // ── Step 3: If still no session, try hash fragment tokens (implicit flow) ──
        if (!session?.user && _initialHash) {
          console.log('[AuthCallback] Trying hash fragment tokens...');
          const hashParams = new URLSearchParams(_initialHash.replace("#", "?"));
          const access_token = hashParams.get("access_token");
          const refresh_token = hashParams.get("refresh_token");
          if (access_token && refresh_token) {
            try {
              const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
              if (!error && data?.session) {
                session = data.session;
                console.log('[AuthCallback] Session from hash tokens:', session.user.email);
              }
            } catch (err) {
              console.warn('[AuthCallback] Hash token error:', err);
            }
          }
        }

        // ── Step 4: Last resort — wait for onAuthStateChange ──
        if (!session?.user) {
          console.log('[AuthCallback] No session yet — waiting for auth state change (10s)...');
          session = await new Promise((resolve) => {
            const { data: { subscription } } = supabase.auth.onAuthStateChange((_evt, s) => {
              if (s?.user?.id) {
                try { subscription.unsubscribe(); } catch (_) {}
                resolve(s);
              }
            });
            setTimeout(() => {
              try { subscription.unsubscribe(); } catch (_) {}
              resolve(null);
            }, 10000);
          });
        }

        if (cancelled) return;

        // ── Done: navigate ──
        if (session?.user) {
          console.log('[AuthCallback] SUCCESS — session for:', session.user.email);
          const user = session.user;
          const displayName =
            (user.user_metadata?.full_name || user.user_metadata?.name) ||
            user.identities?.[0]?.identity_data?.full_name;
          if (displayName) localStorage.setItem("betterhusbandUserName", displayName);

          const redirectParams = `oauth=success${fromOnboarding ? '&from=onboarding' : ''}`;
          navigate(`/login?${redirectParams}`, { replace: true });
        } else {
          console.error('[AuthCallback] FAILED — no session after all attempts');
          console.error('[AuthCallback] Debug: initial URL was:', _initialUrl);
          toast.error("Login failed — please try again");
          navigate("/login", { replace: true });
        }
      } catch (err) {
        console.error("[AuthCallback] Error:", err);
        if (!cancelled) {
          toast.error("Login failed");
          navigate("/login", { replace: true });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [navigate]);

  return <p className="text-center mt-10">Verifying your account...</p>;
}



